import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { ExternalPathString, Link } from "expo-router";
import {
  Download,
  File,
  FileArchive,
  FileSpreadsheet,
  FileText,
  FileType,
} from "lucide-react-native";
import { useMemo } from "react";

type FileAttachmentProps = {
  uri: string;
  name: string;
  mimeType?: string;
  position: "left" | "right";
};

export default function FileAttachment(props: FileAttachmentProps) {
  const isRight = props.position === "right";
  const fileName = props.name.split("/").pop() || props.name;

  const { FileIcon, label } = useMemo(() => {
    const m = props.mimeType ?? "";
    if (/archive|zip|tar|gzip|rar|7z|compressed|bzip2/i.test(m))
      return { FileIcon: FileArchive, label: "Archive" };
    if (/sheet|excel|csv/i.test(m))
      return { FileIcon: FileSpreadsheet, label: "Sheet" };
    if (/word|document|rtf|text/i.test(m))
      return { FileIcon: FileText, label: "Document" };
    if (/pdf/i.test(m)) return { FileIcon: FileText, label: "PDF" };
    if (/presentation|powerpoint/i.test(m))
      return { FileIcon: FileType, label: "Slides" };
    return { FileIcon: File, label: "File" };
  }, [props.mimeType]);

  const ext = fileName.includes(".")
    ? fileName.split(".").pop()?.toUpperCase()
    : null;

  const tileClass = isRight
    ? "bg-background-0/15 border border-background-0/20"
    : "bg-brand-50 border border-brand-100";
  const iconClass = isRight ? "text-background-0" : "text-brand-600";
  const titleClass = isRight ? "text-background-0" : "text-typography-900";
  const subClass = isRight ? "text-background-0/70" : "text-typography-500";
  const downloadBtnClass = isRight
    ? "bg-background-0/20 text-background-0"
    : "bg-background-100 text-typography-700";

  return (
    <Link
      href={props.uri as ExternalPathString}
      target="_blank"
      rel="noreferrer"
    >
      <HStack
        space="sm"
        className="items-center min-w-[260px] max-w-sm py-1 pr-1"
      >
        <Box className={`p-3 rounded-xl ${tileClass}`}>
          <Icon as={FileIcon} className={`w-6 h-6 ${iconClass}`} />
        </Box>
        <VStack className="flex-1 shrink min-w-0" space="xs">
          <Text
            size="sm"
            bold
            className={titleClass}
            isTruncated
            numberOfLines={1}
          >
            {fileName}
          </Text>
          <HStack space="xs" className="items-center">
            <Text
              size="2xs"
              className={`${subClass} uppercase tracking-wider`}
              bold
            >
              {ext ?? label}
            </Text>
          </HStack>
        </VStack>
        <Box className={`p-2 rounded-lg ${downloadBtnClass}`}>
          <Icon as={Download} className="w-4 h-4" />
        </Box>
      </HStack>
    </Link>
  );
}
